import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";
import { Session, User } from "@supabase/supabase-js";

// Define types for the user context
export interface UserContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (
    email: string,
    password: string
  ) => Promise<{ error: any; data: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (password: string) => Promise<{ error: any }>;
}

// Create the context with a default value
const UserContext = createContext<UserContextType | undefined>(undefined);

// Provider component that wraps the app
export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    // Set up initial session and user
    const setupUser = async () => {
      try {
        // Get current session
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          throw error;
        }

        if (session && mounted) {
          setSession(session);
          setUser(session.user);
        }
      } catch (error) {
        console.error("Error fetching user session:", error);
      } finally {
        if (mounted) {
          setIsLoading(false);
          setIsInitialLoad(false);
        }
      }
    };

    setupUser();

    // Set up auth state change listener
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("🔐 Auth state change:", event, !!session?.user);

        if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);

          // Handle auth state changes with navigation
          // Only navigate on actual auth events, not on session refresh/initial load
          if (
            event === "SIGNED_IN" &&
            session?.user &&
            !user &&
            !isInitialLoad
          ) {
            // Only navigate to dashboard if this is a fresh sign-in (user was null before)
            // and not an initial page load/refresh
            console.log("🚀 Navigating to dashboard after fresh sign in");
            navigate("/dashboard");
          } else if (event === "SIGNED_OUT") {
            console.log("🚪 Navigating to login after sign out");
            navigate("/login");
          } else if (event === "PASSWORD_RECOVERY") {
            navigate("/update-password");
          } else if (
            event === "TOKEN_REFRESHED" ||
            (event === "SIGNED_IN" && isInitialLoad)
          ) {
            // Session refresh or initial load - don't navigate, preserve current location
            console.log(
              "🔄 Session refreshed/initial load, staying on current page:",
              location.pathname
            );
          }
        }
      }
    );

    // Clean up auth listener on unmount
    return () => {
      mounted = false;
      authListener.subscription.unsubscribe();
    };
  }, []);

  // Sign in function
  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error };
    } catch (error) {
      return { error };
    }
  };

  // Sign up function
  const signUp = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error("Error signing out:", error);
    }
    // Saved bill-list filters belong to the signed-in user (bill validation).
    try {
      Object.keys(window.sessionStorage)
        .filter((k) => k.startsWith("bv.billFilters"))
        .forEach((k) => window.sessionStorage.removeItem(k));
    } catch {
      // Storage blocked: nothing was saved.
    }
  };

  // Password reset function
  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/update-password`,
      });
      return { error };
    } catch (error) {
      return { error };
    }
  };

  // Update password function
  const updatePassword = async (password: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (!error) {
        navigate("/login");
      }
      return { error };
    } catch (error) {
      return { error };
    }
  };

  // Create value object with state and functions
  const value: UserContextType = {
    user,
    session,
    isLoading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
  };

  // Provide context to children components
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Custom hook for using the user context
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }

  return context;
};
